import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import intl from 'react-intl-universal';
import { createStructuredSelector } from 'reselect';

import { FormikProps } from 'formik';

import { LinearProgress, Grid2 as Grid } from '@mui/material';

import { Board } from '@/models/BoardModel.ts';
import { User } from '@/models/permission/AuthorityModel.ts';
import { Card, Field } from '@/models/card/CardModel.ts';

import { selectActiveBoard } from '../../ducks/Boards';
import {
  selectIsLoadingPossibleResponsible,
  selectPossibleResponsible,
  selectPossibleResponsibleRequested,
} from '../../ducks/Milestones';
import { alertAction } from '../../thunks/Alerts';
import * as milestoneActions from '../../thunks/Milestones';
import {
  getNonMandatoryManualSequenceId,
  populateInitialValues,
  getUpdatedFieldsAndValues,
  getRequiredFields,
  UNIQUE_DOC_ID,
  hasResponsibleField,
} from '../../utils/FieldUtil';

import PageWrapper from '../../common/PageWrapper';
import { withViewModeQuery } from '../../utils/ViewModeUtil';
import Form from '../common/Form';
import MilestoneAttachments from '../common/Attachments';
import MilestoneComments from '../common/Comments';
import Error from '@/common/Error.tsx';

const mapStateToProps = () =>
  createStructuredSelector({
    board: selectActiveBoard(),
    possibleResponsible: selectPossibleResponsible(),
    isLoadingPossibleResponsible: selectIsLoadingPossibleResponsible(),
    possibleResponsibleRequested: selectPossibleResponsibleRequested(),
  });

const mapDispatchToProps = (dispatch: any) => bindActionCreators({ ...milestoneActions }, dispatch);

const MilestoneCreateContainer = ({
  board,
  possibleResponsible,
  isLoadingPossibleResponsible,
  possibleResponsibleRequested,
  createMilestone,
  fetchPossibleResponsible,
}: {
  board: Board;
  possibleResponsible: User[];
  isLoadingPossibleResponsible: boolean;
  possibleResponsibleRequested: { boardId: string };
  createMilestone: any;
  fetchPossibleResponsible: any;
}) => {
  const formRef = useRef<FormikProps<any>>();
  const [closeOnSuccess, setCloseOnSuccess] = useState(false);
  const navigate = useNavigate();
  const { communityId, roomId, boardId, milestoneFolderId } = useParams();
  const milestoneConfig = board.milestones_config.find((config) => config.id === milestoneFolderId);
  const hasResponsible = !!hasResponsibleField({ fieldConfig: milestoneConfig });

  useEffect(() => {
    if (hasResponsible && !isLoadingPossibleResponsible && possibleResponsibleRequested.boardId != boardId) {
      fetchPossibleResponsible({ communityId, roomId, boardId });
    }
  }, []);

  const handleSubmit = ({ values, item }: { values: any; item: any }) => {
    const data = {
      type: 'entry',
      fields: getUpdatedFieldsAndValues({ values, item, fieldsConfig: milestoneConfig?.fields }),
    };

    return createMilestone({ communityId, roomId, boardId, containerId: milestoneFolderId, data }).then(
      (action: any) => {
        alertAction({
          action,
          error: intl.get('milestone.create.error'),
          success: intl.get('milestone.create.success'),
          onSuccess: () =>
            navigate(
              withViewModeQuery(
                closeOnSuccess
                  ? `/${communityId}/${roomId}/boards/${boardId}`
                  : `/${communityId}/${roomId}/boards/${boardId}/milestoneset/${action.payload.milestoneSet}/milestones/${action.payload.milestone.id}`,
              ),
            ),
        });
      },
    );
  };

  const handleCancel = () => navigate(withViewModeQuery(`/${communityId}/${roomId}/boards/${boardId}`));

  const handleSaveClick = (closeForm: boolean) => {
    setCloseOnSuccess(closeForm);

    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  if (hasResponsible && (isLoadingPossibleResponsible || possibleResponsibleRequested.boardId != boardId)) {
    return <LinearProgress />;
  }

  if (!milestoneConfig) {
    return <Error text={intl.get('milestone.not_found')} />;
  }

  // Create new dummy card
  const milestone: Card = {
    type: 'entry',
    fields: milestoneConfig.fields
      .filter((f) => f.type !== 'auto-number')
      .map((f): Field => {
        let value = null;
        if (f.type === 'list' || f.type === 'member') {
          if (f.multiple) {
            value = [];
          } else {
            value = '';
          }
        } else if (f.type === 'number') {
          value = 0;
        }
        return { id: f.id, name: f.name, type: f.type, value: value };
      }),
    permissions: {},
  };

  return (
    <PageWrapper title={intl.get('app_bar.milestone_card')}>
      <Grid container spacing={2}>
        <Grid size={{ sm: 12, md: 8, xs: 12 }}>
          <Form
            formRef={formRef}
            item={milestone}
            initialValues={populateInitialValues({ item: milestone, fields: milestoneConfig.fields })}
            disabledFields={[UNIQUE_DOC_ID, ...getNonMandatoryManualSequenceId(milestoneConfig.fields)]}
            requiredFields={getRequiredFields({ item: milestone, fields: milestoneConfig.fields })}
            type={board.type}
            fieldConfig={milestoneConfig}
            sequenceNumberDigits={board.workflow_config?.sequence_number_digits}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            restrictions={{
              'task-done': () => ({}),
              'task-responsible': () => ({}),
              'rich-text': () => ({}),
              'auto-number': () => ({}),
              'unique-document-id': () => ({}),
              'sequence-number': () => ({}),
              member: () => ({}),
              list: () => ({}),
              date: (fieldConfig, field) => {
                if (fieldConfig.met_date_field === field.id) {
                  return { disableFuture: true };
                }
              },
              string: () => ({}),
              numeric: () => ({}),
            }}
            possibleResponsible={possibleResponsible}
            submitOptions={[
              {
                title: intl.get('common.form.save_and_close'),
                handleClick: () => handleSaveClick(true),
              },
              {
                title: intl.get('common.form.save'),
                default: true,
                handleClick: () => handleSaveClick(false),
              },
            ]}
          />
          <MilestoneAttachments
            activeCommunity={communityId}
            activeRoom={roomId}
            item={milestone}
            attachments={[]}
            isUploading={false}
            isDeleting={false}
          />
        </Grid>
        <Grid size={{ sm: 12, md: 4, xs: 12 }}>
          <MilestoneComments board={board} item={milestone} comments={[]} members={[]} />
        </Grid>
      </Grid>
    </PageWrapper>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(MilestoneCreateContainer);
